import { McpServerEditorData } from '@shared/components/extensions/editors/mcpServerEditorData';
import { CreateMcpServerInput, McpServerModel, UpdateMcpServerInput } from '@shared/models/mcp/McpServerModel';
import {
    localToCreateRequest,
    localToUpdateRequest,
    registrationToCreateInput,
} from '@shared/models/mcp/mcpRegistrationMapping';
import { createMcpServer, updateMcpServer } from '@shared/api/mcp/mcpServersApi';

/**
 * REST-backed {@link McpServerEditorData} for the admin portal. Org admins pre-configure org-scoped
 * connectors from the same editor the desktop app uses. Only NON-SECRET connection config crosses the
 * wire (reusing {@link localToCreateRequest}); secrets, live test-connection, interactive OAuth, and
 * local (stdio) transport are native-desktop-only and disabled here, so each member supplies their own
 * credentials on their device when they enable the connector.
 */
export function createRestMcpServerEditorData(organizationId: string): McpServerEditorData {
    return {
        capabilities: {
            stdioTransport: false,
            secrets: false,
            testConnection: false,
            oauth: false,
            toolSelection: false,
        },

        async createServer(input: CreateMcpServerInput): Promise<McpServerModel> {
            // The editor's CreateMcpServerInput is a structural subset of the local McpServerModel that
            // localToCreateRequest reads (it only touches non-secret config fields).
            const request = localToCreateRequest(input as unknown as McpServerModel, organizationId);
            const result = await createMcpServer(request);
            if (result.resultType !== 'Ok' || !result.data) {
                throw new Error(result.errors?.[0] ?? 'Could not create the connector.');
            }
            return { ...registrationToCreateInput(result.data), id: result.data.id } as unknown as McpServerModel;
        },

        async updateServer(input: UpdateMcpServerInput): Promise<McpServerModel> {
            const request = localToUpdateRequest(input as unknown as McpServerModel);
            const result = await updateMcpServer(input.id, request);
            if (result.resultType !== 'Ok' || !result.data) {
                throw new Error(result.errors?.[0] ?? 'Could not save the connector.');
            }
            return { ...registrationToCreateInput(result.data), id: result.data.id } as unknown as McpServerModel;
        },
    };
}
